/**
 * Common validation utilities
 */

export const ValidationRules = {
  email: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Please enter a valid email address',
  },
  password: {
    minLength: 8,
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
    message:
      'Password must be at least 8 characters with uppercase, lowercase, number, and special character',
  },
  phone: {
    pattern: /^\+?[\d\s\-\(\)]+$/,
    digits: 10,
    message: 'Please enter a valid 10-digit phone number',
  },
  name: {
    pattern: /^[a-zA-Z\s'-]+$/,
    minLength: 2,
    maxLength: 50,
    message:
      'Name must be 2-50 characters and contain only letters, spaces, hyphens, and apostrophes',
  },
  ssn: {
    message: 'Please enter a valid SSN.',
  },
  ein: {
    message: 'Please enter a valid Tax ID.',
  },
}

/**
 * Validate email address
 */
export const isValidEmail = (email: string): boolean => {
  return ValidationRules.email.pattern.test(email.trim())
}

/**
 * Validate password strength
 */
export const isValidPassword = (password: string): boolean => {
  return (
    password.length >= ValidationRules.password.minLength &&
    ValidationRules.password.pattern.test(password)
  )
}

/**
 * Validate phone number
 * Checks for valid format and exactly 10 digits
 */
export const isValidPhone = (phone: string): boolean => {
  const trimmedPhone = phone.trim()

  // Check if the format is valid (allows digits, spaces, hyphens, parentheses, and optional +)
  if (!ValidationRules.phone.pattern.test(trimmedPhone)) {
    return false
  }

  // Extract only digits and check if there are exactly 10 digits
  const digitsOnly = trimmedPhone.replace(/\D/g, '')
  return digitsOnly.length === ValidationRules.phone.digits
}

/**
 * Validate name (first name, last name)
 */
export const isValidName = (name: string): boolean => {
  const trimmedName = name.trim()
  return (
    trimmedName.length >= ValidationRules.name.minLength &&
    trimmedName.length <= ValidationRules.name.maxLength &&
    ValidationRules.name.pattern.test(trimmedName)
  )
}

/**
 * Capitalize the first letter of a name
 * Handles edge cases like empty strings, already capitalized, and names with spaces
 * @param name - The name string to capitalize
 * @returns The name with the first letter capitalized
 * @example
 * capitalizeName('john') // 'John'
 * capitalizeName('MARY') // 'Mary'
 * capitalizeName('o\'brien') // 'O\'brien'
 * capitalizeName('') // ''
 */
export const capitalizeName = (name: string): string => {
  if (!name || name.trim().length === 0) {
    return name
  }

  const trimmed = name.trim()
  // Capitalize first letter and lowercase the rest
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
}

/**
 * Validate SSN (Social Security Number)
 * Format: XXX-XX-XXXX
 * Rules:
 * - 9 digits total
 * - No letters or special symbols (except hyphens)
 * - Can't begin with 000, 666, or 9
 * - 00 and 0000 are invalid for middle and last segments
 */
export const isValidSSN = (ssn: string): boolean => {
  const trimmedSSN = ssn.trim()

  // Remove hyphens for validation
  const digitsOnly = trimmedSSN.replace(/\D/g, '')

  // Must have exactly 9 digits
  if (digitsOnly.length !== 9) {
    return false
  }

  // Extract segments (with or without hyphens)
  let firstSegment = ''
  let middleSegment = ''
  let lastSegment = ''

  if (trimmedSSN.includes('-')) {
    // Format: XXX-XX-XXXX
    const parts = trimmedSSN.split('-')
    if (parts.length !== 3) {
      return false
    }
    firstSegment = parts[0]
    middleSegment = parts[1]
    lastSegment = parts[2]
  } else {
    // Format: XXXXXXXXX (no hyphens)
    firstSegment = digitsOnly.substring(0, 3)
    middleSegment = digitsOnly.substring(3, 5)
    lastSegment = digitsOnly.substring(5, 9)
  }

  // Check if segments contain only digits
  if (
    !/^\d+$/.test(firstSegment) ||
    !/^\d+$/.test(middleSegment) ||
    !/^\d+$/.test(lastSegment)
  ) {
    return false
  }

  // Can't begin with 000, 666, or 9
  if (
    firstSegment === '000' ||
    firstSegment === '666' ||
    firstSegment.startsWith('9')
  ) {
    return false
  }

  // 00 is invalid for middle segment
  if (middleSegment === '00') {
    return false
  }

  // 0000 is invalid for last segment
  if (lastSegment === '0000') {
    return false
  }

  return true
}

/**
 * Validate EIN (Employer Identification Number)
 * Format: XX-XXXXXXX
 * Rules:
 * - 9 digits total
 * - The first two digits identify the IRS processing center
 * - No letters
 */
export const isValidEIN = (ein: string): boolean => {
  const trimmedEIN = ein.trim()

  // Remove hyphens for validation
  const digitsOnly = trimmedEIN.replace(/\D/g, '')

  // Must have exactly 9 digits
  if (digitsOnly.length !== 9) {
    return false
  }

  // Extract segments (with or without hyphens)
  let firstSegment = ''
  let secondSegment = ''

  if (trimmedEIN.includes('-')) {
    // Format: XX-XXXXXXX
    const parts = trimmedEIN.split('-')
    if (parts.length !== 2) {
      return false
    }
    firstSegment = parts[0]
    secondSegment = parts[1]
  } else {
    // Format: XXXXXXXXX (no hyphens)
    firstSegment = digitsOnly.substring(0, 2)
    secondSegment = digitsOnly.substring(2, 9)
  }

  // Check if segments contain only digits
  if (!/^\d+$/.test(firstSegment) || !/^\d+$/.test(secondSegment)) {
    return false
  }

  // First segment must be 2 digits (IRS processing center)
  if (firstSegment.length !== 2) {
    return false
  }

  // Second segment must be 7 digits
  if (secondSegment.length !== 7) {
    return false
  }

  return true
}

/**
 * Validate required field
 */
export const isRequired = (value: string | null | undefined): boolean => {
  return value !== null && value !== undefined && value.trim().length > 0
}

/**
 * Validate minimum length
 */
export const hasMinLength = (value: string, minLength: number): boolean => {
  return value.trim().length >= minLength
}

/**
 * Validate maximum length
 */
export const hasMaxLength = (value: string, maxLength: number): boolean => {
  return value.trim().length <= maxLength
}

/**
 * Validate URL
 */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

/**
 * Validate date (not in the past)
 */
export const isValidFutureDate = (date: string | Date): boolean => {
  const inputDate = new Date(date)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return inputDate >= today
}

/**
 * Validate age (minimum age requirement)
 */
export const isValidAge = (
  birthDate: string | Date,
  minAge: number
): boolean => {
  const birth = new Date(birthDate)
  const today = new Date()
  const age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    return age - 1 >= minAge
  }

  return age >= minAge
}

/**
 * Generic form validation function
 */
export const validateForm = <T extends Record<string, any>>(
  data: T,
  rules: Record<keyof T, Array<(value: any) => string | null>>
): Record<keyof T, string> => {
  const errors: Record<keyof T, string> = {} as Record<keyof T, string>

  for (const field in rules) {
    const fieldRules = rules[field]
    const value = data[field]

    for (const rule of fieldRules) {
      const error = rule(value)
      if (error) {
        errors[field] = error
        break // Stop at first error for this field
      }
    }
  }

  return errors
}

/**
 * Password strength levels
 */
export type PasswordStrengthLevel =
  | 'very-weak'
  | 'weak'
  | 'so-so'
  | 'good'
  | 'strong'

export interface PasswordStrength {
  level: PasswordStrengthLevel
  label: string
  color: string
  segments: number // 1-4 segments filled
  borderColor: string
}

/**
 * Check for common weak patterns in password
 */
const hasWeakPatterns = (password: string): boolean => {
  // Common sequences
  const sequences = [
    '123456',
    'abcdef',
    'qwerty',
    'password',
    'admin',
    'letmein',
    'welcome',
    'monkey',
    '123456789',
    '12345678',
    '12345',
    '1234',
  ]

  const lowerPassword = password.toLowerCase()
  if (sequences.some(seq => lowerPassword.includes(seq))) {
    return true
  }

  // Check for repeated characters (3+ same char in a row)
  if (/(.)\1{2,}/.test(password)) {
    return true
  }

  // Check for simple sequences (123, abc, etc.)
  let hasSequence = false
  for (let i = 0; i < password.length - 2; i++) {
    const char1 = password.charCodeAt(i)
    const char2 = password.charCodeAt(i + 1)
    const char3 = password.charCodeAt(i + 2)
    if (
      (char2 === char1 + 1 && char3 === char2 + 1) ||
      (char2 === char1 - 1 && char3 === char2 - 1)
    ) {
      hasSequence = true
      break
    }
  }

  return hasSequence
}

/**
 * Calculate password strength using industry-standard practices
 * Returns strength level, label, color, and number of segments to fill
 *
 * Scoring criteria:
 * - Length: Progressive scoring (8+ = good, 12+ = excellent, 16+ = bonus)
 * - Character variety: Lowercase, uppercase, numbers, special chars
 * - Pattern detection: Penalize common patterns, sequences, repetitions
 * - Entropy: Reward randomness and character diversity
 */
export const calculatePasswordStrength = (
  password: string
): PasswordStrength => {
  if (!password || password.length === 0) {
    return {
      level: 'very-weak',
      label: 'Very Weak',
      color: '#9E9E9E', // grey
      segments: 1,
      borderColor: '#E0E0E0', // light grey
    }
  }

  let score = 0
  const length = password.length

  // Length scoring (progressive, not binary)
  if (length >= 16) {
    score += 3 // Excellent length
  } else if (length >= 12) {
    score += 2 // Good length
  } else if (length >= 8) {
    score += 1 // Minimum acceptable
  } else if (length >= 6) {
    score += 0.5 // Below recommended but acceptable
  }
  // length < 6 gets no points

  // Character variety checks (each adds to score)
  const hasLowercase = /[a-z]/.test(password)
  const hasUppercase = /[A-Z]/.test(password)
  const hasNumber = /\d/.test(password)
  const hasSpecial = /[^a-zA-Z\d]/.test(password)

  // Count character types present
  const charTypes =
    (hasLowercase ? 1 : 0) +
    (hasUppercase ? 1 : 0) +
    (hasNumber ? 1 : 0) +
    (hasSpecial ? 1 : 0)

  // Reward variety: more character types = higher score
  if (charTypes === 4) {
    score += 3 // All character types present (increased from 2.5)
  } else if (charTypes === 3) {
    score += 2 // Three character types (increased from 1.5)
  } else if (charTypes === 2) {
    score += 1 // Two character types (increased from 0.5)
  }
  // Only one type gets no bonus

  // Bonus for character diversity (unique characters)
  const uniqueChars = new Set(password).size
  const diversityRatio = uniqueChars / length
  if (diversityRatio >= 0.8) {
    score += 1.5 // High diversity (few repeated chars) - increased from 1
  } else if (diversityRatio >= 0.6) {
    score += 1 // Moderate diversity (increased from 0.5)
  } else if (diversityRatio >= 0.5) {
    score += 0.5 // Some diversity
  }

  // Bonus for longer passwords with good variety
  if (length >= 12 && charTypes >= 3) {
    score += 0.5 // Bonus for meeting recommended standards
  }
  if (length >= 16 && charTypes === 4) {
    score += 1 // Extra bonus for excellent passwords
  }

  // Penalize weak patterns
  if (hasWeakPatterns(password)) {
    score -= 2 // Significant penalty for weak patterns
  }

  // Ensure score doesn't go negative
  score = Math.max(0, score)

  // Determine strength level based on score and length
  // Very Weak: score < 1 or length < 4
  if (length < 4 || score < 1) {
    return {
      level: 'very-weak',
      label: 'Very Weak',
      color: '#9E9E9E', // grey
      segments: 1,
      borderColor: '#E0E0E0', // light grey
    }
  }
  // Weak: score 1-3 or (length < 8 and score < 3.5)
  else if (score < 3 || (length < 8 && score < 3.5)) {
    return {
      level: 'weak',
      label: 'Weak',
      color: '#F44336', // red
      segments: 1,
      borderColor: '#F44336', // red
    }
  }
  // So-so: score 3-5 or (length 8-11 with 2+ char types)
  else if (score < 5 || (length >= 8 && length < 12 && charTypes >= 2)) {
    return {
      level: 'so-so',
      label: 'So-so',
      color: '#FF9800', // orange
      segments: 2,
      borderColor: '#FF9800', // orange
    }
  }
  // Good: score 5-7 or (length 12+ with 3+ char types)
  else if (score < 7 || (length >= 12 && charTypes >= 3 && charTypes < 4)) {
    return {
      level: 'good',
      label: 'Good',
      color: '#8BC34A', // light olive green
      segments: 3,
      borderColor: '#8BC34A', // light olive green
    }
  }
  // Strong: score >= 7 or (length >= 12 with all 4 char types and no weak patterns)
  else {
    return {
      level: 'strong',
      label: 'Strong',
      color: '#00BCD4', // teal/cyan
      segments: 4,
      borderColor: '#00BCD4', // teal/cyan
    }
  }
}

/**
 * Common validation rule creators
 */
export const createValidationRules = {
  required:
    (message = 'This field is required') =>
    (value: any) =>
      isRequired(value) ? null : message,

  email:
    (message = ValidationRules.email.message) =>
    (value: string) =>
      !value || isValidEmail(value) ? null : message,

  password:
    (message = ValidationRules.password.message) =>
    (value: string) =>
      !value || isValidPassword(value) ? null : message,

  minLength: (length: number, message?: string) => (value: string) =>
    !value || hasMinLength(value, length)
      ? null
      : message || `Minimum ${length} characters required`,

  maxLength: (length: number, message?: string) => (value: string) =>
    !value || hasMaxLength(value, length)
      ? null
      : message || `Maximum ${length} characters allowed`,

  pattern: (pattern: RegExp, message: string) => (value: string) =>
    !value || pattern.test(value) ? null : message,

  custom:
    (validator: (value: any) => boolean, message: string) => (value: any) =>
      !value || validator(value) ? null : message,
}
